import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import {
  CONTAINER,
  RADIUS,
  SECTION_PADDING,
  SHADOW,
  SPACING,
  TYPOGRAPHY,
} from '../../designTokens';

export const HeroContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
}));

export const RaisedBanner = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1),
  display: 'inline-flex',
  alignItems: 'center',
  padding: `${SPACING.sm} ${SPACING['2xl']} ${SPACING.sm} ${SPACING.xl}`,
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.02em',
  borderRadius: `0 ${RADIUS.full} ${RADIUS.full} 0`,
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    padding: `10px ${SPACING.xl} 10px ${SPACING.md}`,
  },
}));

export const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '3fr 2fr',
  alignItems: 'center',
  gap: SPACING['4xl'],
  maxWidth: CONTAINER.maxWidth,
  margin: '0 auto',
  padding: `${SPACING.xl} ${CONTAINER.gutter} ${SECTION_PADDING.hero.desktop.split(' ')[2]}`,
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: SPACING['2xl'],
    padding: `${SPACING.xl} ${CONTAINER.gutter} ${SECTION_PADDING.hero.mobile.split(' ')[2]}`,
  },
}));

// `minWidth: 0` is critical here. Both columns are CSS Grid items
// (ContentWrapper is `display: grid`) which default to `min-width: auto`
// — meaning the track can grow to fit any child's intrinsic min-content.
// On Android with the issuer's 56px+ display title, the long word
// "Autonomous" forces the grid track wider than the viewport, the
// HeroContainer clips it, and the user sees the text cut off on the
// right (Greenfield Robotics, Jun 2026). Allowing the columns to shrink
// to 0 lets the title's `overflow-wrap` actually kick in and break the
// word as a last resort instead of overflowing the viewport.
export const LeftContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: SPACING['2xl'],
  minWidth: 0,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

export const RightContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: SPACING.xl,
  width: '100%',
  minWidth: 0,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    order: -1,
  },
}));

export const InvestButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.md,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: SPACING.sm,
  },
}));

export const PriceInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

export const DisclaimerText = styled(Typography)(({ theme }) => ({
  fontSize: TYPOGRAPHY.caption.fontSize,
  lineHeight: TYPOGRAPHY.caption.lineHeight,
  maxWidth: '400px',
  paddingTop: SPACING.xs,
  '& a': {
    textDecoration: 'underline',
    '&:hover': {
      opacity: 0.8,
    },
  },
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    maxWidth: '100%',
  },
}));

export const VideoWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  aspectRatio: '16 / 9',
  position: 'relative',
  borderRadius: RADIUS.xl,
  overflow: 'hidden',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

export const StatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: SPACING.md,
  marginTop: SPACING.xl,
  [theme.breakpoints.down('sm')]: {
    gap: SPACING.xs,
  },
}));

export const StatCard = styled(Box)(({ theme }) => ({
  borderRadius: RADIUS.lg,
  padding: `${SPACING.md} ${SPACING.lg}`,
  backgroundColor: 'rgba(24,92,101,0.06)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: `${SPACING.sm} ${SPACING.md}`,
  },
}));

export const VideoOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 5,
}));

export const UnmuteButton = styled(Box)(() => ({
  position: 'absolute',
  width: 80,
  height: 80,
  borderRadius: RADIUS.full,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  pointerEvents: 'auto',
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  color: '#000',
  '&:hover': {
    backgroundColor: '#FFFFFF',
    boxShadow: SHADOW.buttonHover,
  },
}));
